import { useMemo } from 'react';
import {
  GetSchedulingAppointmentTypesResponse,
  GetSchedulingStaffsResponse,
} from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/service.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Chip,
  DropdownField,
  TimeField,
  ChecklistField,
  FormRow,
  Text,
  SearchField,
  DatePickerField,
  DayPicker,
  useFormField,
  useDebouncedValue,
  DaysOfWeek,
} from '@frontend/design-system';
import { AvailabilityRule, AvailabilityScheduleType } from '../../types/availability-rules.types';
import { abbreviateDays, capitalizeFirstLetter, parseIsoDurationToMinutes } from '../../utils/utilities';
import { FormWrapperChildrenProps } from '../form-wrapper';
import { AvailabilityRulesFieldTypes } from './config';
import { RECURRENCE_OPTIONS, TIME_UNIT_OPTIONS, BOOKING_CADENCE_OPTIONS } from './constants';
import { EmptyState } from './empty-state';
import { SubduedTitle, Title } from './titles';

dayjs.extend(customParseFormat);

type ScheduleBreak = NonNullable<AvailabilityRule['scheduleBreaks']>[number];

interface AvailabilityFormProps {
  getFieldProps: FormWrapperChildrenProps<AvailabilityRulesFieldTypes>['getFieldProps'];
  providerOptions: GetSchedulingStaffsResponse;
  servicesOptions: GetSchedulingAppointmentTypesResponse;
  onSelectedDaysChange: (days: DaysOfWeek[]) => void;
  selectedDays: DaysOfWeek[];
  isEditMode: boolean;
  scheduleBreaks: ScheduleBreak[];
  onDeleteBreak: (index: number) => void;
  pendingDeleteBreakIndex?: number | null;
}

const formatBreakTime = (time: string) => {
  if (!time) return '';
  return dayjs(time, 'HH:mm:ss').isValid() ? dayjs(time, 'HH:mm:ss').format('h:mma') : '';
};

const BreakChip = ({
  breakItem,
  isDeleting,
  onDelete,
}: {
  breakItem: ScheduleBreak;
  isDeleting: boolean;
  onDelete: () => void;
}) => {
  const start = formatBreakTime(breakItem.start ?? '');
  const end = formatBreakTime(breakItem.end ?? '');
  if (!start || !end) return null;
  const label = `${start} – ${end}`;

  return (
    <Chip.Tag
      color='gray'
      isRemovable
      onRemoveClick={() => !isDeleting && onDelete()}
      maxWidth={200}
      disabled={isDeleting}
      css={
        isDeleting
          ? {
              opacity: 0.5,
              cursor: 'not-allowed',
              pointerEvents: 'none',
            }
          : undefined
      }
    >
      {label}
    </Chip.Tag>
  );
};

export const AvailabilityForm = ({
  getFieldProps,
  providerOptions,
  servicesOptions,
  onSelectedDaysChange,
  selectedDays,
  isEditMode,
  scheduleBreaks,
  onDeleteBreak,
  pendingDeleteBreakIndex,
}: AvailabilityFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const selectedTimeUnit =
    TIME_UNIT_OPTIONS.find((u) => u.value === getFieldProps('timeUnit').value) ?? TIME_UNIT_OPTIONS[0];
  const appointmentTypeSearch = useFormField({ type: 'text' });
  const debouncedValue = useDebouncedValue(appointmentTypeSearch.value, 500);
  const searchedServices = servicesOptions?.appointmentTypes?.filter((appointmentType) =>
    appointmentType?.name?.toLowerCase().includes(debouncedValue.toLowerCase())
  );

  const recurrenceFieldValue = getFieldProps('recurrence').value;

  const daysOfWeekFieldProps = getFieldProps('daysOfWeek');

  const recurrenceTypeCustomizations = useMemo(() => {
    if (recurrenceFieldValue === AvailabilityScheduleType.WEEKLY) {
      return (
        <>
          <Text
            color='subdued'
            size='small'
            css={{ fontWeight: theme.font.weight.bold, marginBottom: theme.spacing(1) }}
          >
            {t('Day(s) of the week')}
          </Text>
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            <DayPicker
              {...daysOfWeekFieldProps}
              selectedDays={selectedDays || []}
              config={{
                onDayToggle: ({ selectedDays: dayPickerDays }) => {
                  onSelectedDaysChange(dayPickerDays);
                  daysOfWeekFieldProps.onChange({ name: 'daysOfWeek', value: dayPickerDays });
                },
              }}
            />
            {!selectedDays.length && daysOfWeekFieldProps.touched && (
              <Text size='small' color='error'>
                {t('Select a day of the week')}
              </Text>
            )}
            {selectedDays.length > 0 && (
              <Text size='small'>
                {t('Selected: {{selectedDaysOfWeek}}', {
                  selectedDaysOfWeek: abbreviateDays(selectedDays).join(', '),
                })}
              </Text>
            )}
          </div>
        </>
      );
    }

    if (recurrenceFieldValue === AvailabilityScheduleType.RECURRENCE) {
      return (
        <>
          <SubduedTitle> {t('Repeat Pattern')}</SubduedTitle>
          <section css={{ display: 'flex', flexDirection: 'row', gap: theme.spacing(2), justifyContent: 'start' }}>
            <Text as='span' color='light' css={{ marginTop: theme.spacing(1) }}>
              {t('Every')}
            </Text>
            <FormRow
              cols={[50, 50]}
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'unset',
                width: '100%',
                marginBottom: 0,
              }}
            >
              <DropdownField {...getFieldProps('interval')} label={''}>
                {Array.from(
                  { length: selectedTimeUnit.max - selectedTimeUnit.min + 1 },
                  (_, i) => i + selectedTimeUnit.min
                ).map((unit: number) => (
                  <DropdownField.Option key={unit} value={unit.toString()}>
                    {unit}
                  </DropdownField.Option>
                ))}
              </DropdownField>
              <DropdownField {...getFieldProps('timeUnit')} label={''}>
                {TIME_UNIT_OPTIONS.map((unit) => (
                  <DropdownField.Option key={unit.value} value={unit.value}>
                    {t('{{unit}}', { unit: unit.label })}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </FormRow>
          </section>
        </>
      );
    }

    if (recurrenceFieldValue === AvailabilityScheduleType.SPECIFIC_DATE) {
      return null;
    }
    return null;
  }, [
    getFieldProps,
    t,
    selectedDays,
    onSelectedDaysChange,
    daysOfWeekFieldProps,
    selectedTimeUnit.min,
    selectedTimeUnit.max,
    recurrenceFieldValue,
  ]);

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: theme.spacing(0.5),
        paddingTop: theme.spacing(1),
        width: `calc(100% - 8px)`,
      }}
    >
      <hr css={{ marginBottom: theme.spacing(2), border: `1px solid ${theme.colors.neutral20}` }} />

      <Title>{t('Schedule')}</Title>
      <SubduedTitle>{t('Recurrence')}</SubduedTitle>

      <FormRow cols={[100]} css={{ marginBottom: theme.spacing(2) }}>
        <DropdownField {...getFieldProps('recurrence')} label={''}>
          {RECURRENCE_OPTIONS.map((option) => {
            const label = capitalizeFirstLetter(option.label);
            return (
              <DropdownField.Option key={option.value} value={option.value} css={{ textTransform: 'capitalize' }}>
                {t('{{label}}', { label })}
              </DropdownField.Option>
            );
          })}
        </DropdownField>
      </FormRow>

      {recurrenceTypeCustomizations}

      {/* NOTE - TrueLark doesn't support a start date for weekly recurrences. This value is omitted */}
      {recurrenceFieldValue !== AvailabilityScheduleType.WEEKLY && (
        <>
          <SubduedTitle>
            {recurrenceFieldValue === AvailabilityScheduleType.SPECIFIC_DATE ? t('Date') : t('Start Date')}
          </SubduedTitle>
          <DatePickerField {...getFieldProps('startDate')} label={''} css={{ maxWidth: '100%' }} />
        </>
      )}

      <FormRow cols={[50, 50]} css={{ marginTop: theme.spacing(0), marginBottom: theme.spacing(2) }}>
        <section css={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <SubduedTitle>{t('Start Time')}</SubduedTitle>
          <TimeField {...getFieldProps('startTime')} label={''} showAdornment />
        </section>
        <section css={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <SubduedTitle>{t('End Time')}</SubduedTitle>
          <TimeField
            {...getFieldProps('endTime')}
            minTime={getFieldProps('startTime').value ?? ''}
            label={''}
            showAdornment
          />
        </section>
      </FormRow>
      {isEditMode && !!scheduleBreaks?.length && (
        <>
          <SubduedTitle css={{ marginTop: theme.spacing(1) }}>{t('Breaks')}</SubduedTitle>
          <Text color='subdued' size='small' css={{ marginBottom: theme.spacing(1) }}>
            {t(
              'This break was set up in TrueLark. You can delete it here, and manage your breaks from the Rule Exceptions tab.'
            )}
          </Text>
          <div css={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), marginBottom: theme.spacing(1) }}>
            {scheduleBreaks.map((breakItem, index) => (
              <BreakChip
                key={`${breakItem.start ?? 'start'}-${breakItem.end ?? 'end'}-${index}`}
                breakItem={breakItem}
                onDelete={() => onDeleteBreak?.(index)}
                isDeleting={pendingDeleteBreakIndex === index}
              />
            ))}
          </div>
        </>
      )}

      <hr css={{ margin: `${theme.spacing(4)} 0`, border: `1px solid ${theme.colors.neutral20}` }} />

      <Title>{t('Provider & Services')}</Title>
      <SubduedTitle>{t('Provider*')}</SubduedTitle>

      <DropdownField {...getFieldProps('provider')} label={''}>
        {providerOptions?.staffs
          ?.sort((a, b) =>
            (a.name || a.firstName + ' ' + a.lastName || '').localeCompare(
              b.name || b.firstName + ' ' + b.lastName || ''
            )
          )
          .map((provider) => (
            <DropdownField.Option
              key={provider.id}
              value={provider.id || provider.name || provider.firstName + ' ' + provider.lastName}
            >
              {provider.name || provider.firstName + ' ' + provider.lastName}
            </DropdownField.Option>
          ))}
      </DropdownField>
      <Text color='subdued' size='small' css={{ marginTop: theme.spacing(0.5) }}>
        {t('The provider who will perform the service')}
      </Text>

      <SubduedTitle>{t('Services*')}</SubduedTitle>
      <section
        css={{
          border: `1px solid ${theme.colors.neutral20}`,
          borderRadius: theme.borderRadius.medium,
          padding: theme.spacing(2),
          maxHeight: 300,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div css={{ marginBottom: theme.spacing(1) }}>
          <SearchField name='service-search' placeholder={t('Search')} {...appointmentTypeSearch} />
        </div>
        <div css={{ flex: 1, overflowY: 'auto' }}>
          {searchedServices?.length === 0 && appointmentTypeSearch.value ? (
            <EmptyState description={t('No services match your search')} />
          ) : (
            <ChecklistField {...getFieldProps('appointmentTypes')} error={''} label={''}>
              {searchedServices
                ?.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((appointmentType) => {
                  if (!appointmentType || !appointmentType.bookable) return null;
                  return (
                    <ChecklistField.Option key={appointmentType.id} name={appointmentType.id ?? ''}>
                      {appointmentType.name}{' '}
                      {appointmentType.totalDuration
                        ? `(${parseIsoDurationToMinutes(appointmentType.totalDuration)} min)`
                        : ''}
                    </ChecklistField.Option>
                  );
                })}
            </ChecklistField>
          )}
        </div>
        {getFieldProps('appointmentTypes').error && (
          <Text color='error' size='small' css={{ marginTop: theme.spacing(0.5) }}>
            {getFieldProps('appointmentTypes').error}
          </Text>
        )}
      </section>

      <hr css={{ margin: `${theme.spacing(4)} 0`, border: `1px solid ${theme.colors.neutral20}` }} />

      <Title>{t('Booking Rules')}</Title>
      <SubduedTitle>{t('Booking Cadence')}</SubduedTitle>
      <FormRow cols={[100]}>
        <DropdownField {...getFieldProps('bookingCadence')} label={''}>
          {BOOKING_CADENCE_OPTIONS.map((option) => {
            return (
              <DropdownField.Option key={option.value} value={option.value} css={{ textTransform: 'capitalize' }}>
                {t('{{unit}}', { unit: option.label })}
              </DropdownField.Option>
            );
          })}
        </DropdownField>
      </FormRow>
    </div>
  );
};
